import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, FileSignature, ShieldCheck, Download } from "lucide-react";
import { toast } from "sonner";

const money = (n) => (n == null ? "—" : `£${Number(n).toLocaleString()}`);

export default function OfferSign({ kind = "offer" }) {
  const { token } = useParams();
  const base = kind === "contract" ? "/contracts" : "/offers";
  const docLabel = kind === "contract" ? "Contract of employment" : "Offer of employment";
  const [offer, setOffer] = useState(null);
  const [err, setErr] = useState(false);
  const [name, setName] = useState("");
  const [result, setResult] = useState(null); // "signed" | "declined"

  const load = () => api.get(`${base}/sign/${token}`).then((r) => setOffer(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const act = async (decision) => {
    if (decision === "accept" && !name.trim()) { toast.error("Please type your full name to sign"); return; }
    try {
      await api.post(`${base}/sign/${token}`, { decision, signature_name: name });
      setResult(decision === "accept" ? "signed" : "declined");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    try { const r = await api.get(`${base}/sign/${token}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Your signed copy isn't ready yet — please try again shortly"); }
  };

  if (err) return <Shell><div className="text-center"><p className="text-lg font-semibold text-[#0B1A2F]">Offer unavailable</p><p className="text-sm text-slate-500 mt-2">This offer link is invalid or has already been actioned.</p></div></Shell>;
  if (!offer) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;

  const already = offer.status === "signed" || offer.status === "declined" || result;
  if (already) {
    const signed = result === "signed" || offer.status === "signed";
    return (
      <Shell>
        <div className="text-center" data-testid="offer-result">
          {signed ? <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={44} /> : <XCircle className="mx-auto text-rose-500 mb-3" size={44} />}
          <p className="text-xl font-bold text-[#0B1A2F]">{signed ? "Offer accepted" : "Offer declined"}</p>
          <p className="text-sm text-slate-500 mt-2">{signed ? `Thank you, ${offer.signature_name || name}. Your acceptance has been recorded and CASS has been notified.` : "You've declined this offer. CASS has been notified."}</p>
          {signed && <p className="text-[11px] text-slate-400 mt-3 flex items-center justify-center gap-1"><ShieldCheck size={13} />Securely e-signed & timestamped</p>}
          {signed && <Button variant="outline" className="mt-4" onClick={downloadPdf} data-testid="download-signed-pdf-btn"><Download size={15} className="mr-2" />Download your signed copy</Button>}
        </div>
      </Shell>
    );
  }

  return (
    <Shell wide>
      <div className="flex items-center gap-2 text-[#2EA3FD] mb-1"><FileSignature size={18} /><span className="text-xs font-semibold uppercase tracking-wider">{docLabel}</span></div>
      <h1 className="text-2xl font-extrabold text-[#0B1A2F]">{offer.job_title}</h1>
      <p className="text-sm text-slate-500">Prepared for {offer.candidate_name}</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-5 text-sm">
        <Field label="Salary" value={money(offer.salary)} />
        <Field label="Hours / week" value={offer.hours_per_week ?? "—"} />
        <Field label="Start date" value={offer.start_date ? fmtDate(offer.start_date) : "—"} />
        <Field label="Holiday (weeks)" value={offer.holiday_weeks ?? "—"} />
        <Field label="Probation" value={offer.probation_months ? `${offer.probation_months} months` : "—"} />
        <Field label="Offer expires" value={offer.expiry_date ? fmtDate(offer.expiry_date) : "—"} />
      </div>
      {offer.notes && <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600 mt-4 whitespace-pre-wrap">{offer.notes}</div>}

      <div className="border-t border-slate-200 mt-6 pt-5">
        <p className="text-sm font-semibold text-[#0B1A2F] mb-2">Sign to accept</p>
        <p className="text-xs text-slate-500 mb-3">Type your full legal name below. This acts as your electronic signature and will be recorded with the date, time and your IP address.</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="max-w-sm mb-4" data-testid="offer-signature-input" />
        <div className="flex gap-3">
          <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6] font-semibold" onClick={() => act("accept")} data-testid="offer-accept-btn"><CheckCircle2 size={16} className="mr-2" />Accept & sign</Button>
          <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => act("decline")} data-testid="offer-decline-btn">Decline</Button>
        </div>
      </div>
    </Shell>
  );
}

const Field = ({ label, value }) => (<div><p className="text-xs text-slate-400">{label}</p><p className="font-semibold text-[#0B1A2F]">{value}</p></div>);

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-[#EEF2F7]">
      <header className="h-16 bg-gradient-to-r from-[#091E36] via-[#0B1A2F] to-[#0F2C50] text-white flex items-center px-6">
        <span className="text-2xl font-extrabold text-[#2EA3FD]">CASS</span><span className="ml-2 text-lg font-semibold">Careers</span>
      </header>
      <div className={`${wide ? "max-w-2xl" : "max-w-md"} mx-auto p-6`}>
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">{children}</div>
      </div>
    </div>
  );
}
